/**
 * NewsThreadCard — Threads/Bluesky-style social card for legal news.
 *
 * Source: F-FE-HOTNEWS §3 + design source §2.4 (Threads-style social-feed).
 *
 * Layout (vertical):
 *   - Header row: source avatar (32px) + source name + relative time
 *   - Headline (bold 18-20px)
 *   - AI summary (quote-block) OR raw_excerpt placeholder when AI failed
 *   - Citation chip + tag chips
 *   - Micro-action row: Like / Save / Share / Hỏi Asklaw CTA
 *
 * Mobile-first 360px+. Dark mode via CSS variables. Reduced-motion via
 * global rule (no per-component handling).
 */

import { Link } from 'react-router-dom';
import { useLang } from '../../use-lang';
import { resolveHotNews, type PlayfulTag } from '../../i18n/hotnews';
import { NewsTagChip } from './NewsTagChip';
import { NewsMicroActionsRow } from './NewsMicroActionsRow';

/**
 * AI disclosure block - Luat AI 2026 Dieu 28 transparency. Mirrors backend
 * `AiDisclosure` shape in src/news/api/public.ts (field names MUST match
 * exactly). Defined here (card module) to avoid an import cycle with
 * lib/news-api.ts, which re-exports this type.
 */
export interface NewsAiDisclosure {
  /** Model attribution, vd "Asklaw AI (Gemini 2.5 Flash)". */
  generated_by: string;
  /** Human reviewer attribution, vd "Asklaw Editorial". */
  reviewed_by: string;
  /** Unix epoch sec when reviewed; null when never reviewed. */
  reviewed_at: number | null;
  /** ISO 8601 mirror of reviewed_at for human-readable rendering. */
  reviewed_at_iso: string | null;
}

export interface NewsCardData {
  id: string;
  slug: string;
  source_id: string;
  source_name: string;
  source_url: string;
  headline: string;
  ai_summary: string | null;
  ai_summary_status: 'pending' | 'generated' | 'failed' | 'rejected';
  raw_excerpt: string | null;
  topic: string | null;
  detail_mode: 'case_event' | 'new_law' | 'mixed';
  primary_vbpl_doc_id?: string | null;
  primary_vbpl_provision?: string | null;
  citation_count?: number;
  tags: PlayfulTag[];
  published_at: number; // unix epoch seconds
  /** Luat AI 2026 Dieu 28 transparency block (backend always sends). */
  ai_disclosure?: NewsAiDisclosure;
}

export interface NewsThreadCardProps {
  news: NewsCardData;
}

export function NewsThreadCard({ news }: NewsThreadCardProps) {
  const { lang } = useLang();
  const copy = resolveHotNews(lang);

  const relTime = formatRelativeTime(news.published_at, copy);

  return (
    <article
      className="news-thread-card"
      aria-labelledby={`news-headline-${news.id}`}
      style={cardStyle}
    >
      <header style={headerStyle}>
        <SourceAvatar sourceId={news.source_id} sourceName={news.source_name} />
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={sourceLabelStyle}>
            {copy.card_source_prefix}{' '}
            <Link
              to={`/news/source/${news.source_id}`}
              style={{ color: 'inherit', fontWeight: 600 }}
            >
              {news.source_name}
            </Link>
          </span>
          <time
            dateTime={new Date(news.published_at * 1000).toISOString()}
            style={timeStyle}
          >
            {relTime}
          </time>
        </div>
      </header>

      <h3
        id={`news-headline-${news.id}`}
        style={headlineStyle}
      >
        <Link
          to={`/news/${news.slug}`}
          style={{ color: 'inherit', textDecoration: 'none' }}
        >
          {news.headline}
        </Link>
      </h3>

      {news.ai_summary_status === 'generated' && news.ai_summary ? (
        <blockquote style={summaryStyle}>
          <span style={summaryHeadRowStyle}>
            <span style={summaryLabelStyle}>{copy.card_summary_label}</span>
            {news.ai_disclosure ? (
              <span
                style={aiBadgeStyle}
                title={news.ai_disclosure.generated_by}
                aria-label={`Tao boi ${news.ai_disclosure.generated_by}`}
              >
                AI - {news.ai_disclosure.generated_by}
              </span>
            ) : null}
          </span>
          <p style={{ margin: '4px 0 0' }}>{news.ai_summary}</p>
        </blockquote>
      ) : news.raw_excerpt ? (
        <p style={excerptStyle}>{news.raw_excerpt}</p>
      ) : null}

      {(news.tags.length > 0 ||
        (news.citation_count !== undefined && news.citation_count > 0)) && (
        <div style={chipRowStyle}>
          {news.citation_count !== undefined && news.citation_count > 0 ? (
            <span style={citationChipStyle} aria-label={copy.card_citation_label}>
              <CitationIcon />
              {news.citation_count} {copy.card_citation_label}
            </span>
          ) : null}
          {news.tags.map((tag) => (
            <NewsTagChip key={tag} tag={tag} size="sm" />
          ))}
        </div>
      )}

      <NewsMicroActionsRow newsId={news.id} slug={news.slug} headline={news.headline} />
    </article>
  );
}

function SourceAvatar({ sourceName }: { sourceId: string; sourceName: string }) {
  // Avatar = first 2 letters of source name, colored circle.
  const initials = sourceName
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div
      aria-hidden
      style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, var(--asklaw-orange-soft), var(--asklaw-orange))',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontFamily: 'var(--font-sans)',
        fontSize: 12,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initials || 'A'}
    </div>
  );
}

function CitationIcon() {
  return (
    <svg
      aria-hidden
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      style={{ marginRight: 4 }}
    >
      <path
        d="M3 2h4l2 2v6H3V2Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M7 2v2h2" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M5 6h3M5 8h2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function formatRelativeTime(
  publishedAt: number,
  copy: ReturnType<typeof resolveHotNews>,
): string {
  const now = Date.now() / 1000;
  const diff = now - publishedAt;
  if (diff < 60) return copy.card_time_relative_now;
  if (diff < 3600) {
    const n = Math.floor(diff / 60);
    return copy.card_time_minutes.replace('{n}', String(n));
  }
  if (diff < 86400) {
    const n = Math.floor(diff / 3600);
    return copy.card_time_hours.replace('{n}', String(n));
  }
  const n = Math.floor(diff / 86400);
  return copy.card_time_days.replace('{n}', String(n));
}

const cardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: 20,
  background: 'var(--asklaw-paper)',
  border: '1px solid var(--asklaw-border)',
  borderRadius: 14,
  boxShadow: 'var(--shadow-card)',
  marginBottom: 14,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
};

const sourceLabelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  color: 'var(--asklaw-ink-soft)',
};

const timeStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  color: 'var(--asklaw-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const headlineStyle: React.CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-sans)',
  fontSize: 'clamp(17px, 2.4vw, 20px)',
  fontWeight: 700,
  letterSpacing: '-0.01em',
  lineHeight: 1.35,
  color: 'var(--asklaw-ink)',
};

const summaryStyle: React.CSSProperties = {
  margin: 0,
  padding: '12px 14px',
  background: 'var(--asklaw-cream-deep)',
  borderLeft: '3px solid var(--asklaw-orange)',
  borderRadius: '0 8px 8px 0',
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--asklaw-ink-soft)',
};

const summaryLabelStyle: React.CSSProperties = {
  display: 'inline-block',
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: 'var(--asklaw-orange-deep)',
  fontWeight: 600,
};

const summaryHeadRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
};

// Compact AI disclosure badge - reuses existing color tokens (no new CSS
// system). Luat AI 2026 Dieu 28: model attribution visible near ai_summary.
const aiBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '1px 7px',
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.02em',
  borderRadius: 999,
  background: 'var(--asklaw-blue-soft, #d6e6f4)',
  color: 'var(--asklaw-navy)',
  border: '1px solid color-mix(in srgb, var(--asklaw-navy) 18%, transparent)',
};

const excerptStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--asklaw-ink-soft)',
};

const chipRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  alignItems: 'center',
};

const citationChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  fontSize: 11,
  fontWeight: 600,
  borderRadius: 999,
  background: 'var(--asklaw-blue-soft, #d6e6f4)',
  color: 'var(--asklaw-navy)',
  border: '1px solid color-mix(in srgb, var(--asklaw-navy) 18%, transparent)',
};
